import { useMutation, useQuery } from "@apollo/client";
import PictureAsPdfOutlined from "@mui/icons-material/PictureAsPdfOutlined";
import { Alert, Button, TextField, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { SET_DAILY_REPORT_NOTE } from "../dashboardReports.queries";
import { browserTimezone, Granularity } from "../reportFormat";
import { ReportFilterState } from "../ReportFilters";
import { REPORT_RETENTION_CURVE, REPORT_RETENTION_DRIVERS } from "../scorecard.queries";
import { DayWindow, toDay } from "../scorecardFormat";
import { buildDailyReportPdf, DAILY_PDF_HEADINGS, DailyReportData, DailySection } from "./dailyReportPdf";
import { buildEngagementPdf, EngagementPdfData } from "./engagementPdf";
import { dayHeading } from "./pdfDoc";
import { MEANINGS_LABEL, OptionCheckbox, PreparedByField, ReportPdfDialog, usePreparedBy } from "./ReportPdfDialog";
import { buildRetentionPdf, RetentionPdfData } from "./retentionPdf";
import { buildScorecardPdf, ScorecardMetric } from "./scorecardPdf";

const ExportButton: React.FC<{ disabled?: boolean; onClick: () => void }> = ({ disabled, onClick }) => (
  <Button variant="outlined" startIcon={<PictureAsPdfOutlined />} disabled={disabled} onClick={onClick}>
    Export PDF
  </Button>
);

const timezone = () => browserTimezone() ?? "America/New_York";

/** "Atlanta, Georgia, United States", or null for everywhere. */
export function regionLabel(filters: Pick<ReportFilterState, "city" | "state" | "country">): string | null {
  const parts = [filters.city, filters.state, filters.country].map((option) => option?.displayName || option?.name).filter(Boolean);
  return parts.length ? parts.join(", ") : null;
}

export const rangeOf = (filters: Pick<ReportFilterState, "from" | "to">): DayWindow => ({ from: toDay(filters.from), to: toDay(filters.to) });

// ---- Daily report -------------------------------------------------------------

export interface DailyNote {
  section: DailySection;
  text: string;
}

/**
 * The note that opens a section of a day's report, kept on the server so the
 * whole team sees the same one. Saved when the field loses focus.
 */
export const DailyNoteField: React.FC<{
  day: string;
  section: DailySection;
  saved: string;
  onSaved: () => Promise<unknown>;
  label?: string;
}> = ({ day, section, saved, onSaved, label }) => {
  const [draft, setDraft] = useState(saved);
  const [setNote, { loading }] = useMutation(SET_DAILY_REPORT_NOTE);
  useEffect(() => setDraft(saved), [saved, day]);

  const save = async () => {
    if (draft.trim() === saved.trim()) return;
    try {
      await setNote({ variables: { day, section, text: draft } });
      await onSaved();
    } catch (err: any) {
      toast.error(err?.message ?? "Could not save the note.");
    }
  };
  return (
    <TextField
      label={label ?? `Note under “${DAILY_PDF_HEADINGS[section]}”`}
      helperText={loading ? "Saving…" : `For the report of ${dayHeading(day)}. Saved for everyone when you click away.`}
      value={draft}
      onChange={(event) => setDraft(event.target.value)}
      onBlur={save}
      multiline
      minRows={2}
      maxRows={8}
      size="small"
      fullWidth
      inputProps={{ maxLength: 2000 }}
    />
  );
};

export const DailyReportPdfButton: React.FC<{
  report: (DailyReportData & { notes: DailyNote[] }) | null;
  onNotesSaved: () => Promise<unknown>;
}> = ({ report, onNotesSaved }) => {
  const [open, setOpen] = useState(false);
  const [preparedBy, setPreparedBy] = usePreparedBy();
  const [includeExtras, setIncludeExtras] = useState(false);
  const [includeMeanings, setIncludeMeanings] = useState(true);
  const day = report?.days[report.days.length - 1] ?? "";
  const saved = (section: DailySection) => report?.notes.find((note) => note.section === section)?.text ?? "";
  const notes = { LEVELS: saved("LEVELS"), GROWTH: saved("GROWTH"), ACTIVITY: saved("ACTIVITY"), CALL: saved("CALL") };
  const doc = report && report.days.length ? buildDailyReportPdf(report, { preparedBy, notes, includeExtras, includeMeanings }) : null;

  return (
    <>
      <ExportButton disabled={!report} onClick={() => setOpen(true)} />
      <ReportPdfDialog open={open} onClose={() => setOpen(false)} title="Daily report PDF" doc={doc}>
        <Typography variant="body2" color="text.secondary">
          Laid out as the team's daily report. The date at the top is the last day in the range.
        </Typography>
        <PreparedByField value={preparedBy} onChange={setPreparedBy} />
        {report &&
          (["LEVELS", "GROWTH", "ACTIVITY"] as DailySection[]).map((section) => (
            <DailyNoteField key={section} day={day} section={section} saved={notes[section]} onSaved={onNotesSaved} />
          ))}
        <OptionCheckbox
          value={includeExtras}
          onChange={setIncludeExtras}
          label="Add rows the template does not have (lead generation, live rooms, the weekly call)"
        />
        <OptionCheckbox value={includeMeanings} onChange={setIncludeMeanings} label="End with what the numbers mean" />
        {report?.chatMessagesSource === "APP" && (
          <Alert severity="warning" variant="outlined">
            getstream could not be read, so chat messages come from the app's own log, which misses older app builds.
          </Alert>
        )}
        {report && report.days[0] < report.phoneVerifiedDatesExactSince && (
          <Alert severity="info" variant="outlined">
            Before {dayHeading(report.phoneVerifiedDatesExactSince)}, Members (name, phone number, email) counts a member on the day they
            registered: the day they verified their phone was not recorded then.
          </Alert>
        )}
      </ReportPdfDialog>
    </>
  );
};

// ---- Scorecard --------------------------------------------------------------------

export const ScorecardPdfButton: React.FC<{
  card: { timezone: string; current: DayWindow; previous: DayWindow; metrics: ScorecardMetric[] } | null;
  scope: Record<string, string | null | undefined>;
  region: string | null;
}> = ({ card, scope, region }) => {
  const [open, setOpen] = useState(false);
  const [preparedBy, setPreparedBy] = usePreparedBy();
  const [includeMeanings, setIncludeMeanings] = useState(true);
  const [cohortSize, setCohortSize] = useState<"QUARTER" | "MONTH">("QUARTER");
  // The same queries the tab's sections make, so they are usually in the cache already.
  const curve = useQuery(REPORT_RETENTION_CURVE, { variables: { input: { ...scope, cohortSize } }, skip: !open });
  const drivers = useQuery(REPORT_RETENTION_DRIVERS, { variables: { input: scope }, skip: !open });
  const curveData = curve.data?.reportRetentionCurve;
  const ready = card && !curve.loading && !drivers.loading;
  const doc = ready
    ? buildScorecardPdf(
        {
          current: card.current,
          previous: card.previous,
          timezone: card.timezone,
          region,
          metrics: card.metrics,
          curve: curveData ? { since: curveData.since, cohortSize, overall: curveData.overall, cohorts: curveData.cohorts } : null,
          drivers: drivers.data?.reportRetentionDrivers ?? null,
        },
        { preparedBy, includeMeanings },
      )
    : null;

  return (
    <>
      <ExportButton disabled={!card} onClick={() => setOpen(true)} />
      <ReportPdfDialog open={open} onClose={() => setOpen(false)} title="Scorecard PDF" doc={doc}>
        <Typography variant="body2" color="text.secondary">
          Every number on the Scorecard for both stretches of days, the change between them and its target, then months after joining
          and what makes members stay.
        </Typography>
        <PreparedByField value={preparedBy} onChange={setPreparedBy} />
        <OptionCheckbox value={includeMeanings} onChange={setIncludeMeanings} label={MEANINGS_LABEL} />
        <div>
          <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 0.5 }}>
            Months after joining, grouped
          </Typography>
          <ToggleButtonGroup size="small" exclusive value={cohortSize} onChange={(_, value) => value && setCohortSize(value)}>
            <ToggleButton value="QUARTER">By quarter</ToggleButton>
            <ToggleButton value="MONTH">By month</ToggleButton>
          </ToggleButtonGroup>
        </div>
        {(curve.error || drivers.error) && (
          <Alert severity="warning" variant="outlined">
            Part of the report could not be loaded and is left out: {(curve.error ?? drivers.error)?.message}
          </Alert>
        )}
      </ReportPdfDialog>
    </>
  );
};

// ---- Retention and Engagement ----------------------------------------------------

export const RetentionPdfButton: React.FC<{
  report: Pick<RetentionPdfData, "overall" | "cohorts"> | null;
  filters: ReportFilterState;
  cohortSize: Granularity;
}> = ({ report, filters, cohortSize }) => {
  const [open, setOpen] = useState(false);
  const [preparedBy, setPreparedBy] = usePreparedBy();
  const [includeMeanings, setIncludeMeanings] = useState(true);
  const doc = report
    ? buildRetentionPdf(
        { ...report, range: rangeOf(filters), timezone: timezone(), region: regionLabel(filters), cohortSize },
        { preparedBy, includeMeanings },
      )
    : null;
  return (
    <>
      <ExportButton disabled={!report} onClick={() => setOpen(true)} />
      <ReportPdfDialog open={open} onClose={() => setOpen(false)} title="Retention PDF" doc={doc}>
        <Typography variant="body2" color="text.secondary">
          Day 1, 7 and 30 retention for the dates and region chosen above, overall and by the {cohortSize === "MONTH" ? "month" : "week"}{" "}
          members registered.
        </Typography>
        <PreparedByField value={preparedBy} onChange={setPreparedBy} />
        <OptionCheckbox value={includeMeanings} onChange={setIncludeMeanings} label={MEANINGS_LABEL} />
      </ReportPdfDialog>
    </>
  );
};

export const EngagementPdfButton: React.FC<{
  report: Pick<EngagementPdfData, "totals" | "events" | "periods"> | null;
  filters: ReportFilterState;
}> = ({ report, filters }) => {
  const [open, setOpen] = useState(false);
  const [preparedBy, setPreparedBy] = usePreparedBy();
  const [includeMeanings, setIncludeMeanings] = useState(true);
  const doc = report
    ? buildEngagementPdf(
        { ...report, range: rangeOf(filters), timezone: timezone(), region: regionLabel(filters), granularity: filters.granularity },
        { preparedBy, includeMeanings },
      )
    : null;
  return (
    <>
      <ExportButton disabled={!report} onClick={() => setOpen(true)} />
      <ReportPdfDialog open={open} onClose={() => setOpen(false)} title="Engagement PDF" doc={doc}>
        <Typography variant="body2" color="text.secondary">
          The Engagement tab for the dates and region chosen above.
        </Typography>
        <PreparedByField value={preparedBy} onChange={setPreparedBy} />
        <OptionCheckbox value={includeMeanings} onChange={setIncludeMeanings} label={MEANINGS_LABEL} />
      </ReportPdfDialog>
    </>
  );
};
